import { appURLs } from "@/app/app-urls.mjs";
import type { GameDefinition } from "@/app/game-definition.mjs";
import type { MatchFull, MatchStart } from "@/data-models/csgo-game-events.mjs";
import type { CSMatch } from "@/data-models/csgo-match-deprecated.mjs";
import type { Matchlist } from "@/data-models/csgo-matches.mjs";
import type { CsgoPlayer } from "@/data-models/csgo-player.mjs";
import type { PlayerMapStatsCollection } from "@/data-models/csgo-player-map-stats.mjs";
import type { PlayerStats } from "@/data-models/csgo-player-stats.mjs";
import type { PlayerWeaponStatsAggregate } from "@/data-models/csgo-player-weapon-stats.mjs";
import type { MapsStats } from "@/data-models/csgo-stats-maps.mjs";
import type { WeaponsStats } from "@/data-models/csgo-stats-weapons.mjs";
import { DATABASE_LINKS } from "@/game-cs2-marketing/constants/links.mjs";
import type { GameMode } from "@/game-csgo/constants.mjs";
import { GAME_SYMBOL_CS2 } from "@/game-csgo/definition-symbol.mjs";
import { OVERLAY_IDS } from "@/game-csgo/refs.mjs";
import routes from "@/game-csgo/routes.mjs";
import GameIconBoxCS2 from "@/inline-assets/game-icon-box-cs2.svg";
import FriendGameIconBoxCSGO from "@/inline-assets/game-icon-friend-box-csgo.svg";

// This whole file is actually the definition for CS2, not CSGO.
// The directory needs to be renamed.
export { GAME_SYMBOL_CS2 };

const CSGO_OVERLAY_SETTINGS = {
  isCombatOverlayEnabled: true,
  isBenchmarkOverlayEnabled: true,
  positions: Object.values(OVERLAY_IDS).reduce((acc, id) => {
    acc[id] = { x: 0, y: 0, isDragged: false };
    return acc;
  }, {}),
  dynamicStats: {
    damagePerRound: true,
    score: true,
    kills: true,
    accuracy: true,
    benchmarkOption: 0,
  },
};

const definition = {
  gameSymbol: GAME_SYMBOL_CS2,
  icon: `${appURLs.CDN_PLAIN}/blitz/ui/images/icons/games/small/CSGO-small.svg`,
  iconShape: GameIconBoxCS2,
  iconBox: GameIconBoxCS2,
  iconBadge: FriendGameIconBoxCSGO,

  imageHero: `${appURLs.CDN_PLAIN}/blitz/games/assest/hero-logo/hero-logo-csgo.png`,
  imageBanner: `${appURLs.CDN}/blitz/library/banner-csgo.png`,
  imageKeyart: `${appURLs.CDN}/blitz/ui/img/game-cover/cs2-min.webp`,
  imageWordmark: `${appURLs.CDN}/blitz/ui/img/gamecover/name-cs2-4e9bc.webp`,

  isActive: true,
  fullName: ["common:games.cs2.long", "Counter-Strike 2"],
  friendlyName: ["common:games.cs2.short", "CS2"],
  preferFriendlyName: false,
  shortName: "cs2" as const,
  backendName: "CSGO" as const,
  gameColor: "#C3943F",

  settings: () => import("@/hub-settings/CSGOSettings.jsx"),
  routes,

  overlays: {
    csgoLeaderboard: {
      title: ["overlays:csgoLeaderboardTitle", "Leaderboard"],
      description: [
        "overlays:csgoLeaderboardDescription",
        "Track your combat metrics in real time. Compare stats to the best in the lobby.",
      ],
      conflictDisclaimer: [
        "overlays:windowedModeOnly",
        "This overlay only works in Windowed mode.",
      ],
      images: {
        small: `${appURLs.CDN}/blitz/ui/img/settings/csOverlay/leaderboard-overlay.webp`,
        large: `${appURLs.CDN}/blitz/ui/img/settings/csOverlay/leaderboard-portrait-2x.webp`,
      },
      tags: [],
      date: new Date(),
      noToggle: true,
      key: ["isCsgoLeaderboardEnabled"],
      // native: true,
    },
    csgoScoreboard: {
      title: ["overlays:csgoScoreboardTitle", "Scoreboard"],
      description: [
        "overlays:csgoScoreboardDescription",
        "Track your team’s metrics against the enemy team in real time. Compare your stats with other players in the lobby.",
      ],
      conflictDisclaimer: [
        "overlays:windowedModeOnly",
        "This overlay only works in Windowed mode.",
      ],
      images: {
        small: `${appURLs.CDN}/blitz/ui/img/settings/csOverlay/scoreboard-overlay.webp`,
        large: `${appURLs.CDN}/blitz/ui/img/settings/csOverlay/scoreboard-portrait-2x.webp`,
      },
      tags: [],
      date: new Date(),
      noToggle: true,
      key: ["isCsgoScoreboardEnabled"],
      // native: true,
    },
  },
  overlaySettings: CSGO_OVERLAY_SETTINGS,

  IPC: () => import("@/game-csgo/init.mjs"),

  meta: {
    game: {
      title: [
        "home:meta.home.cs2.title",
        "CS2 Stats, Leaderboards, Skins, and Best Tracker!",
      ],
      description: [
        "home:meta.home.csgo.description",
        "Explore a world of insights with our CS2 Gun Stats and Map Stats, providing you an edge in mastering the Best Guns and Best Weapons in Counter Strike 2. Dive into our Leaderboards and Tracker to see where you stand among the elite, and unlock your full potential in the intense battlegrounds of CS2.",
      ],
    },
    pros: [
      {
        name: "VT ZywOo",
        src: "vt-zywoo.webp",
        id: "STEAM_1:1:76700232",
      },
      {
        name: "NAVI s1mple",
        src: "navi-simple.webp",
        id: "STEAM_1:1:36968273",
      },
      {
        name: "G2 NiKo",
        src: "g2-niko.webp",
        id: "STEAM_1:0:40708825",
      },
      {
        name: "FaZe Twistzz",
        src: "faze-twistzz.webp",
        id: "STEAM_1:1:27994738",
      },
    ],
    bg: `${appURLs.CDN}/blitz/ui/img/game-bg/cs2-home-bg-centered.webp`,
    players: "14M+",
    matches: "8M+",
    guides: [
      () => import("@/game-csgo/HomeMapsTile.jsx"),
      () => import("@/game-csgo/HomeWeaponsTile.jsx"),
    ],
    features: [
      {
        title: ["csgo.features.profile-page.title", "Profile Page"],
        description: [
          "csgo.features.profile-page.description",
          "Track valuable metrics over time to measure your gaming performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/profile-page.webp`,
      },
      {
        title: [
          "csgo.features.dynamic-stats-overlay.title",
          "Dynamic Stats Overlay",
        ],
        description: [
          "csgo.features.dynamic-stats-overlay.description",
          "Track key combat metrics in real-time to better guage performance",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/dynamic-stats-overlay.webp`,
      },
      {
        title: [
          "csgo.features.post-match-insights-overlay.title",
          "Post Match Insights Overlay",
        ],
        description: [
          "csgo.features.post-match-insights-overlay.description",
          "Automatically view and benchmark key stats as soon as your match ends",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/post-match-insights-overlay.webp`,
      },
      {
        title: ["csgo.features.leaderboard.title", "Leaderboard"],
        description: [
          "csgo.features.leaderboard.description",
          "See how you stack up against the best players from around the world",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/leaderboard.webp`,
      },
      {
        title: ["csgo.features.weapon-statistics.title", "Weapon Statistics"],
        description: [
          "csgo.features.weapon-statistics.description",
          "Check out the most popular Weapons played",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/weapon-statistics.webp`,
      },
      {
        title: ["csgo.features.map-statistics.title", "Map Statistics"],
        description: [
          "csgo.features.map-statistics.description",
          "Check out the most popular Maps played",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/map-statistics.webp`,
      },
      {
        title: ["csgo.features.weapon-guide.title", "Weapon Guide"],
        description: [
          "csgo.features.weapon-guide.description",
          "Check out in depth stats on every weapon in the game",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/weapon-guide.webp`,
      },
      {
        title: ["csgo.features.lineup-guide.title", "Lineup Guide"],
        description: [
          "csgo.features.lineup-guide.description",
          "Review the best attacking and defending positions",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/lineup-guide.webp`,
      },
      {
        title: ["cs2.features.match-replay.title", "Match Replay"],
        description: [
          "cs2.features.match-replay.description",
          "Explore 2D replays with individual player stats, game events, loadout an more!",
        ],
        image: `${appURLs.CDN}/blitz/ui/img/homepage/cs2/match-replay.webp`,
      },
    ],
    articles: [
      {
        createdAt: new Date("2023-03-15"),
        title: [
          "home:articles.csgo.dynamic-stats-overlay.title",
          "Dynamic Stats Overlay",
        ],
        subtitle: [
          "home:articles.csgo.dynamic-stats-overlay.subtitle",
          "Track key combat metrics in real time and compare them to your teammates, or the best in the lobby.",
        ],
        background: `${appURLs.CDN}/homepage/news/csgo-dynamic-stats.webp`,
        link: {
          href: `/overlays/cs2`,
          text: ["common:overlay.view", "View Overlay"],
        },
      },
    ],
    homeData: () => import("@/game-csgo/home-fetch-csgo-data.mjs"),
  },

  navLinks: [
    {
      href: `/cs2/stats/maps`,
      text: ["common:navigation.statistics", "Statistics"],
      menu: [
        [
          {
            items: [
              {
                text: ["common:navigation.maps", "Maps"],
                href: `/cs2/stats/maps`,
              },
              {
                text: ["common:navigation.weapons", "Weapons"],
                href: `/cs2/stats/weapons`,
              },
            ],
          },
        ],
      ],
    },
    {
      href: `/cs2/leaderboard`,
      text: ["common:navigation.leaderboards", "Leaderboards"],
    },
    {
      href: `/cs2/marketplace`,
      text: ["common:navigation.marketplace", "Marketplace"],
    },
    {
      text: ["cs2:navigation.lineups", "Lineups"],
      href: `/cs2/lineups`,
    },
    // {
    //   href: `/cs2/database`,
    //   text: ["common:navigation.database", "Database"],
    //   menu: DATABASE_LINKS,
    // },
    ...DATABASE_LINKS,
    {
      href: `/overlays/cs2`,
      text: ["common:navigation.overlays", "Overlays"],
      excludeSearch: true,
    },
  ],
  homepageFeatures: [
    "blitz-search",
    {
      type: "contentful-articles",
      title: ["common:news.title", "News"],
      description: [
        "common:news.descriptionGame",
        "What’s new with {{gameName}}",
        {
          gameName: "Counter-Strike 2",
        },
      ],
    },
    "blitz-articles",
    "blitz-guides",
    "blitz-game-features",
  ],
} satisfies GameDefinition<typeof GAME_SYMBOL_CS2>;

declare module "@/__main__/app-state.mjs" {
  interface AppState {
    csgo: {
      profiles: { [profileId: string]: CsgoPlayer };
      matches: { [matchId: string]: CSMatch };
      matchlists: { [profileId: string]: Matchlist };
      playerStats: {
        [profileId: string]: {
          [gameMode: string]: PlayerStats;
        };
      };
      playerWeaponStats: {
        [profileId: string]: PlayerWeaponStatsAggregate;
      };
      live: unknown;
      liveGame: MatchFull | MatchStart | null;
      lastGameMode: GameMode | null;
      stats: {
        weapons: { [mode: string]: WeaponsStats };
        maps: { [mode: string]: MapsStats };
      };
      playerMapStats: {
        [profileId: string]: {
          [gameMode: string]: PlayerMapStatsCollection;
        };
      };
    };
  }
}

export default definition;
